import {
  Globe,
  Router,
  Network,
  Wifi,
  AlertTriangle,
  ShieldCheck,
  Wrench,
  TrendingUp,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const frustrations = [
  "Je systemen zijn traag, maar niemand weet waarom.",
  "WiFi valt weg zodra je de vergaderkamer inloopt.",
  "De printer is 'niet gevonden' — voor de zoveelste keer.",
  "Videobellen hapert precies op het verkeerde moment.",
  "Bestanden uploaden duurt een eeuwigheid.",
  "Collega's zitten op elkaars netwerk zonder dat je het weet.",
];

const components = [
  {
    icon: Globe,
    number: "01",
    title: "Internet",
    subtitle: "De verbinding met de buitenwereld",
    text: "Alles begint hier. Zonder internet geen e-mail, geen cloud, geen Teams, geen online kassa. Internet is de snelweg die jouw bedrijf verbindt met de rest van de wereld. En net als bij een echte snelweg: als die dichtslaat, staat alles stil. Daarom is een snelle, stabiele verbinding zo belangrijk.",
    color: "bg-hero-gradient",
  },
  {
    icon: Router,
    number: "02",
    title: "Router / Firewall",
    subtitle: "De hersenen én de bewaker",
    text: "De router is de baas van je netwerk. Hij bepaalt waar al je data naartoe gaat en houdt ongewenste bezoekers buiten. Zie het als de portier van een club: hij beslist wie er in mag en wie niet. Rinqel levert altijd een managed router, zodat wij kunnen meekijken, bijsturen en problemen oplossen — zonder dat jij er iets van merkt.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Network,
    number: "03",
    title: "Switch",
    subtitle: "De slimme stekkerdoos",
    text: "Een switch verbindt al je apparaten via kabels met het netwerk. Computers, printers, camera's, kassa's — alles wordt aangesloten op de switch. Hoe beter de switch, hoe sneller en betrouwbaarder je apparaten met elkaar communiceren. Denk aan een verdeelstation: alles komt hier samen.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: Wifi,
    number: "04",
    title: "Access Points",
    subtitle: "WiFi voor iedereen, overal",
    text: "Access points zorgen ervoor dat je overal in je pand sterk WiFi-signaal hebt. Voor je medewerkers, voor gasten, en eventueel voor meerdere bedrijven in hetzelfde gebouw. Geen dode hoeken, geen geklaag over slechte verbinding. Gewoon overal snel en stabiel draadloos internet.",
    color: "bg-hero-gradient",
  },
];

const benefits = [
  {
    icon: ShieldCheck,
    title: "Updates & patches",
    desc: "Wij houden je netwerk up-to-date. Jij hoeft er niets voor te doen.",
    color: "bg-hero-gradient",
  },
  {
    icon: Wrench,
    title: "Storing? Wij lossen het op",
    desc: "Eén telefoontje en we zijn er. Snel, zonder gedoe.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: TrendingUp,
    title: "Uitbreiden zonder moeite",
    desc: "Meer medewerkers of locaties? Geregeld, zonder grote investeringen.",
    color: "bg-rinqel-green-gradient",
  },
];

const NetworkContent = () => {
  return (
    <>
      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-1 w-10 rounded-full bg-hero-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-primary">Wat is een netwerk?</span>
              <span className="h-1 w-10 rounded-full bg-hero-gradient" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              De{" "}
              <span className="text-gradient">snelweg</span>
              {" "}tussen al je apparaten
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Je laptop, printer, telefoon, kassa, beveiligingscamera — alles
              is met elkaar verbonden via je netwerk. Zonder goed netwerk staan
              al die apparaten als losse eilandjes naast elkaar. Ze kunnen niet
              samenwerken, niet delen, en jij kunt niet efficiënt werken.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              En het mooie is: als je netwerk wél goed werkt, merk je er niks van.
              Alles draait gewoon. Precies zoals het hoort.
            </p>
          </div>
        </div>
      </section>

      {/* Frustrations */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center mb-10">
            <div className="mb-5 flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Wat gebeurt er als je netwerk{" "}
              <span className="text-destructive">niet deugt?</span>
            </h2>
            <p className="mt-3 text-muted-foreground">
              Herkenbaar? Dan is de kans groot dat je netwerk toe is aan een upgrade.
            </p>
          </div>

          <div className="mx-auto grid max-w-3xl gap-3 sm:grid-cols-2">
            {frustrations.map((item, i) => (
              <div
                key={i}
                className="group relative flex items-start gap-4 rounded-2xl border-2 border-destructive/40 bg-destructive/5 p-5 overflow-hidden transition-all duration-200 hover:border-destructive/70 hover:bg-destructive/10 hover:-translate-y-0.5"
                style={{ boxShadow: "0 0 0 0 hsl(0 84% 60% / 0), 0 2px 12px -2px hsl(0 84% 60% / 0.12)" }}
              >
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: "inset 0 0 0 2px hsl(0 84% 60% / 0.5)" }} />
                <div className="shrink-0 flex h-8 w-8 items-center justify-center rounded-xl bg-destructive/15 border border-destructive/30">
                  <span className="text-destructive text-sm font-black leading-none">✕</span>
                </div>
                <p className="text-sm leading-relaxed text-foreground/70 pt-1">{item}</p>
              </div>
            ))}
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-muted-foreground">
            Een slecht netwerk kost je{" "}
            <span className="font-semibold text-foreground">tijd, geld en geduld</span>.
            Het goede nieuws? Het is allemaal op te lossen.
          </p>
        </div>
      </section>

      {/* Components explained */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-1 w-10 rounded-full bg-rinqel-blue-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary">De bouwstenen</span>
              <span className="h-1 w-10 rounded-full bg-rinqel-blue-gradient" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Waar bestaat een netwerk uit?
            </h2>
            <p className="mt-4 text-muted-foreground">
              Vier bouwstenen, uitgelegd in de volgorde waarin ze samenwerken — van buiten naar binnen.
            </p>
          </div>

          <div className="mx-auto max-w-4xl space-y-5">
            {components.map((comp) => {
              const Icon = comp.icon;
              return (
                <div
                  key={comp.title}
                  className="flex flex-col gap-0 rounded-2xl border border-border bg-card shadow-card overflow-hidden sm:flex-row sm:items-stretch"
                >
                  {/* Colored sidebar */}
                  <div className={`flex shrink-0 flex-row items-center gap-4 px-6 py-5 sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:w-28 sm:py-0 ${comp.color}`}>
                    <span className="text-3xl font-black text-white/40 leading-none">{comp.number}</span>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  {/* Content */}
                  <div className="flex-1 p-6">
                    <h3 className="text-lg font-bold">{comp.title}</h3>
                    <p className="text-sm font-semibold text-gradient">{comp.subtitle}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{comp.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rinqel regelt alles */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-1 w-10 rounded-full bg-rinqel-green-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-rinqel-green">Beheerd netwerk</span>
              <span className="h-1 w-10 rounded-full bg-rinqel-green-gradient" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Rinqel regelt{" "}
              <span className="text-gradient">alles voor je</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wij leveren niet alleen je netwerk, wij beheren het ook. Updates,
              patches, monitoring, troubleshooting — het zit er allemaal bij in.
              Gaat er iets kapot? Wij vervangen het. Wil je uitbreiden? Geregeld.
              Eén aanspreekpunt, geen kastje naar de muur.
            </p>
          </div>

          <div className="mx-auto grid max-w-3xl gap-5 sm:grid-cols-3">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className={`relative overflow-hidden flex flex-col gap-3 rounded-2xl p-6 ${b.color} shadow-card`}
                >
                  <div className="absolute -right-4 -bottom-4 h-20 w-20 rounded-full bg-white/10" />
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="relative">
                    <p className="font-bold text-white text-sm">{b.title}</p>
                    <p className="mt-1 text-xs text-white/80">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 relative overflow-hidden rounded-2xl bg-rinqel-blue-gradient p-8 md:p-10 shadow-card">
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
            <div className="absolute -left-4 bottom-0 h-28 w-28 rounded-full bg-white/5" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-xl">
                <h3 className="text-xl font-bold text-white md:text-2xl">
                  Klaar voor een netwerk dat gewoon werkt?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/80">
                  Maandelijks opzegbaar, dagelijks beheerd. Geen verrassingen op de factuur.
                </p>
                <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/90">
                  {["Gratis adviesgesprek", "Inclusief beheer", "Maandelijks opzegbaar"].map((item) => (
                    <li key={item} className="flex items-center gap-1.5">
                      <CheckCircle className="h-4 w-4 text-white/70 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-3 shrink-0 sm:flex-row">
                <Button className="bg-white text-secondary hover:bg-white/90 font-semibold" size="lg" asChild>
                  <Link to="/configurator">
                    Bereken je prijs <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button className="border border-white/30 bg-white/10 text-white hover:bg-white/20 font-semibold" size="lg" asChild>
                  <Link to="/contact">Neem contact op</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NetworkContent;
